// src/pages/healthcare/appointments/AppointmentDrawer.tsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { Button, Space, message, Modal, Tag, Alert, Typography, Collapse } from "antd";
import { SaveOutlined, SyncOutlined, LinkOutlined, FileTextOutlined, CheckCircleOutlined, LoadingOutlined, ExclamationCircleOutlined, PlayCircleOutlined, CalendarOutlined, CloseCircleOutlined, UserOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

// Shared design-system components
import DetailDrawer from "../../../components/shared/DetailDrawer";

// Drawer tab content components
import GeneralTab from "./appDrawerComponents/GeneralTab";
import ScreeningPanel from "./appDrawerComponents/ScreeningPanel";
import ConsultGuidancePanel from "../consultation/consultationComponents/ConsultGuidancePanel";

// Nested modals
import EditPatientModal from "../patient/PatientModal";
import EditUserModal from "../../../components/editUserModal/EditUserModal";

// Types
import { Appointment, AppointmentSaveRequest, AppointmentType, CompletedAppointment } from "../../../types/appointments/AppointmentTypes";
import { Transcript } from "../../../types/appointments/ConsultTypes";
import { User } from "../../../types/admin/UserTypes";
import { Patient, isCustomerIdentity } from "../../../types/healthcare/PatientTypes";
import { TeamMember } from "../../../types/admin/TeamTypes";

// Import the api
import { setNewAppointment, updateAppointment, fetchSingleCompletedAppointmentById, fetchAppointmentPractitioners, addPractitionerToAppointment, removePractitionerFromAppointment, fetchPatientAppointmentTypeUsage } from "../../../api/healthcare/Appointment/AppointmentApi";
import { AppointmentPractitioner } from "../../../types/appointments/AppointmentTypes";
import { fetchPatient } from "../../../api/healthcare/PatientApi";
import { fetchTeamMember } from "../../../api/admin/teamApi";
import { generateConsultation, fetchConsultJobStatus, fetchConsultSession, fetchPendingConsultJobs } from "../../../api/healthcare/Consultation/ConsultApi";
import { listVitalSigns, getVitalSignsSchema } from "../../../api/healthcare/vitalSignsApi";
import { buildVitalsReadings } from "../../../utils/healthcare/vitalsReadings";
import ConsultProgressModal from "./ConsultProgressModal";
import ArchivedChatView from "../../../components/consult/ArchivedChatView";
import ArchivedTranscriptView from "../../../components/consult/ArchivedTranscriptView";
import ConsultRecordingPlayer, { SeekTarget } from "../../../components/consult/ConsultRecordingPlayer";
import { parseChat } from "../../../utils/consult/consultChat";
import { parseStoredTranscripts } from "../../../hooks/consult/useConsultTranscripts";
import { parseSpeakerMap, rosterForRecord } from "../../../utils/consult/speakerNames";
import { useFinalTranscript } from "../../../hooks/consult/useFinalTranscript";
import { extractRoomId, generateConsultationLink, generateRoomId } from "../../../utils/consult/consultationLink";
import type { UserUuid } from "../../../types/identity";

const { Text } = Typography;

interface AppointmentDrawerProps {
	user: User;
	isOpen: boolean;
	onClose: (id?: number) => void;
	onStartConsultation: (appointment: Appointment) => void;
	selectedAppointment: Appointment;
	setSelectedAppointment: React.Dispatch<React.SetStateAction<Appointment | undefined>>;
	teamMembers: TeamMember[];
	selectedTeamMember: TeamMember | null;
	setSelectedTeamMember: React.Dispatch<React.SetStateAction<TeamMember | null>>;
	selectedPatient: Patient;
	setSelectedPatient: React.Dispatch<React.SetStateAction<Patient>>;
}

/** One entry of healthcare's `unavailable_block` 409 payload. */
interface UnavailableBlock {
	starts_at: string;
	ends_at: string;
	reason: string | null;
}

interface GuidanceStatus {
	exists: boolean;
	status: "pending" | "processing" | "completed" | "failed" | "cancelled" | null;
	sessionId: number | null;
	jobUuid: string | null;
	errorMessage?: string;
	progress?: number;
}

const AppointmentDrawer: React.FC<AppointmentDrawerProps> = ({
	user,
	isOpen,
	onClose,
	onStartConsultation,
	selectedAppointment,
	setSelectedAppointment,
	teamMembers,
	selectedTeamMember,
	setSelectedTeamMember,
	selectedPatient,
	setSelectedPatient,
}) => {
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [errorMessages, setErrorMessages] = useState<string[]>([]);
	const [activeTab, setActiveTab] = useState("Appointment");
	const [isLock, setIsLock] = useState<boolean>(false);
	const [patientModalOpen, setPatientModalOpen] = useState<boolean>(false);
	const [practitionerModalOpen, setPractitionerModalOpen] = useState<boolean>(false);
	const [updatedAppointment, setUpdatedAppointment] = useState<Appointment>(selectedAppointment);
	const [isGeneratingConsult, setIsGeneratingConsult] = useState(false);
	const [completedConsult, setCompletedConsult] = useState<CompletedAppointment | null>(null);
	const [consultJobUuid, setConsultJobUuid] = useState<string | null>(null);
	const [consultProgressModalVisible, setConsultProgressModalVisible] = useState(false);
	const [guidanceStatus, setGuidanceStatus] = useState<GuidanceStatus>({
		exists: false,
		status: null,
		sessionId: null,
		jobUuid: null,
	});
	const [checkingGuidance, setCheckingGuidance] = useState(false);
	const [secondaryPractitioners, setSecondaryPractitioners] = useState<AppointmentPractitioner[]>([]);
	const [appointmentTypes, setAppointmentTypes] = useState<AppointmentType[]>([]);
	const [selectedAppointmentType, setSelectedAppointmentType] = useState<AppointmentType | null>(null);

	// This appointment's in-consult vitals, read from the canonical patient_vital_signs
	const { data: patientVitalSigns = [] } = useQuery({
		queryKey: ["vitalSigns", selectedAppointment.patient_id],
		queryFn: () => listVitalSigns(selectedAppointment.patient_id),
		enabled: !!selectedAppointment.patient_id && selectedAppointment.id !== 0,
	});
	const { data: vitalSignsSchema = [] } = useQuery({
		queryKey: ["vitalSignsSchema"],
		queryFn: getVitalSignsSchema,
		staleTime: 1000 * 60 * 60,
	});
	const consultVitalsRecord = useMemo(
		() => patientVitalSigns.find((r) => r.appointment_id === updatedAppointment.id),
		[patientVitalSigns, updatedAppointment.id],
	);
	const consultVitalsReadings = useMemo(
		() => (consultVitalsRecord ? buildVitalsReadings(consultVitalsRecord, vitalSignsSchema) : []),
		[consultVitalsRecord, vitalSignsSchema],
	);

	// The archived chat. Empty on every in-person consult and every historical row.
	const archivedChat = useMemo(() => parseChat(completedConsult?.chat), [completedConsult]);

	// The archived transcript, same double-encoded-JSON tolerance as the chat.
	const archivedTranscript = useMemo(() => parseStoredTranscripts(completedConsult?.transcript), [completedConsult]);
	const archivedSpeakerMap = useMemo(() => parseSpeakerMap(completedConsult?.speaker_map), [completedConsult]);
	const transcriptRoster = useMemo(
		() => rosterForRecord(archivedTranscript, { practitioner: selectedTeamMember, teamMembers, patient: selectedPatient }),
		[archivedTranscript, selectedTeamMember, teamMembers, selectedPatient],
	);

	useFinalTranscript({
		appointmentId: updatedAppointment.id,
		status: completedConsult?.transcript_status,
		enabled: !!completedConsult && archivedTranscript.length > 0,
		onFinal: setCompletedConsult,
	});

	// On a `final` row an empty `audio_files` means no recording was retained
	const recordingFileId = typeof completedConsult?.audio_files === "string" && completedConsult.audio_files.trim() ? completedConsult.audio_files : null;
	// talk writes `audio_files` with the final pass, so a live row that has a transcript is still waiting on its recording.
	const recordingPending = !recordingFileId && completedConsult?.transcript_status === "live" && archivedTranscript.length > 0;
	const [seekTarget, setSeekTarget] = useState<SeekTarget | null>(null);
	const [playhead, setPlayhead] = useState(0);
	const [recordingReady, setRecordingReady] = useState(false);

	// Keyed on the recording, not the row: a live→final swap keeps the same loaded player, and `onReady` fires only once per load.
	useEffect(() => {
		setSeekTarget(null);
		setPlayhead(0);
		setRecordingReady(false);
	}, [recordingFileId]);

	const markRecordingReady = useCallback(() => setRecordingReady(true), []);

	// `start`/`end` exist only on final-pass rows, so a live transcript is never seekable.
	const canSeek = completedConsult?.transcript_status === "final" && recordingReady;
	const activeSegmentId = useMemo(
		() => (canSeek ? archivedTranscript.find((t) => typeof t.start === "number" && typeof t.end === "number" && playhead >= t.start && playhead < t.end)?.id ?? null : null),
		[canSeek, archivedTranscript, playhead],
	);
	const seekNonceRef = useRef(0);
	const handleSegmentSelect = useCallback((t: Transcript) => {
		if (typeof t.start === "number") setSeekTarget({ seconds: t.start, nonce: ++seekNonceRef.current });
	}, []);

	// S10 (ClickUp 86d3yhdwc) — this patient's usage of the CAPPED appointment types. Capped
	// types only, so an uncapped type is simply absent and nothing renders for it.
	const { data: appointmentTypeUsage = [] } = useQuery({
		queryKey: ["appointmentTypeUsage", selectedPatient.id],
		queryFn: () => fetchPatientAppointmentTypeUsage(selectedPatient.id),
		enabled: !!selectedPatient.id,
	});

	/**
	 * The selected type's usage when the patient is AT their cap, otherwise null.
	 */
	const atCapUsage = useMemo(() => {
		const typeId = selectedAppointmentType?.id ?? updatedAppointment.appointment_type_id;
		if (!typeId) return null;

		return appointmentTypeUsage.find((u) => u.appointment_type_id === typeId && u.bookings_remaining === 0) ?? null;
	}, [appointmentTypeUsage, selectedAppointmentType, updatedAppointment.appointment_type_id]);

	useEffect(() => {
		const getPatient = async () => {
			const { data } = await fetchPatient(selectedAppointment.patient_id.toString());
			setSelectedPatient(data);
		};
		const getPrac = async () => {
			const { data } = await fetchTeamMember(selectedAppointment.practitioner_id || user.id);
			setSelectedTeamMember(data);
		};

		if (selectedAppointment) {
			setUpdatedAppointment(selectedAppointment);

			if (selectedAppointment.id !== 0) {
				setIsLock(true);
				checkGuidanceStatus(selectedAppointment.id);
				fetchAppointmentPractitioners(selectedAppointment.id)
					.then(list => setSecondaryPractitioners(list.filter(p => p.role === 'secondary')))
					.catch(() => {});
				if (selectedAppointment.status?.toLowerCase() === "completed" || selectedAppointment.status?.toLowerCase() === "post consult completed") {
					loadCompletedConsult(selectedAppointment.id, selectedAppointment.patient_id);
				}
			}
			if (selectedAppointment.patient_id !== "") {
				getPatient();
			}
			if (selectedAppointment.practitioner_id !== "") {
				getPrac();
			}
		}
	}, [selectedAppointment]);

	// Check if guidance exists and its status
	const loadCompletedConsult = async (appointmentId: number, patientId: UserUuid) => {
		try {
			const { data } = await fetchSingleCompletedAppointmentById(appointmentId);
			setCompletedConsult(data);
			if (data) {
				setActiveTab("Report");
			}
		} catch (error) {
			console.error("Failed to load completed consult:", error);
		}
	};

	// Check if guidance exists and its status
	const checkGuidanceStatus = async (appointmentId: number) => {
		setCheckingGuidance(true);
		try {
			// Try to fetch the consult session
			const response = await fetchConsultSession(appointmentId);

			if (response.session) {
				// Guidance exists and is completed
				setGuidanceStatus({
					exists: true,
					status: "completed",
					sessionId: response.session.id,
					jobUuid: null,
				});
			} else {
				try {
					const jobsResponse = await fetchPendingConsultJobs(appointmentId);

					const activeSessions = jobsResponse?.sessions || [];
					if (activeSessions.length > 0) {
						const latestSession = activeSessions[0];
						// Check job status
						if (latestSession.consult_job) {
							const jobStatus = await fetchConsultJobStatus(latestSession.consult_job.job_uuid);
							setGuidanceStatus({
								exists: false,
								status: jobStatus.status,
								sessionId: null,
								jobUuid: latestSession.consult_job.job_uuid,
								errorMessage: jobStatus.error_message,
								progress: jobStatus.progress?.progress_percentage,
							});
						}
					} else {
						setGuidanceStatus({
							exists: false,
							status: null,
							sessionId: null,
							jobUuid: null,
						});
					}
				} catch {
					setGuidanceStatus({
						exists: false,
						status: null,
						sessionId: null,
						jobUuid: null,
					});
				}
			}
		} catch (error: any) {
			console.error("Failed to check guidance status:", error);
		} finally {
			setCheckingGuidance(false);
		}
	};

	// CU-14ygtkje5r2: a customer keeps the booking, but a consultation is clinical work for a patient only.
	const customerAttendee = isCustomerIdentity(selectedPatient);

	const handleStartConsultation = async () => {
		if (customerAttendee) return;

		// If this is a Cliniko appointment with a booking link, open Cliniko
		if (updatedAppointment.cliniko_id && (updatedAppointment.location === "Online" || updatedAppointment.location === "online")) {
			window.open(updatedAppointment.consultation_link, "_blank");

			// Update status to in consultation
			let app = { ...updatedAppointment, status: "In consultation" };
			try {
				await updateAppointment(app);
				setUpdatedAppointment(app);
			} catch (error) {
				console.error("Failed to update appointment status", error);
			}

			// Reset tab
			setActiveTab("Appointment");
		} else {
			// Resolves a Cliniko telehealth_url too — regenerating would strand the patient on the link they were emailed.
			let roomId = extractRoomId(updatedAppointment.consultation_link);

			let finalAppointment = updatedAppointment;

			// A link we cannot parse is still a link the patient may hold — refuse rather than overwrite it.
			if (!roomId && updatedAppointment.consultation_link) {
				message.error("This appointment's consultation link is not valid");
				setActiveTab("Appointment");
				return;
			}

			if (!roomId) {
				const consultLink = generateConsultationLink();
				// The room id must be the link's own segment, or practitioner and patient join different rooms.
				roomId = extractRoomId(consultLink) as string;

				const updatedApp = {
					...updatedAppointment,
					consultation_link: consultLink,
					status: "In consultation",
				};

				try {
					const { data } = await updateAppointment(updatedApp);
					setUpdatedAppointment(data);
					finalAppointment = data;
					console.log(`✓ Updated appointment with room ID: ${roomId}`);
				} catch (error) {
					console.error("Failed to update appointment with room ID", error);
					message.error("Failed to prepare consultation");
					setActiveTab("Appointment");
					return;
				}
			} else {
				// Update status to in consultation
				let app = { ...updatedAppointment, status: "In consultation" };
				try {
					await updateAppointment(app);
					setUpdatedAppointment(app);
					finalAppointment = app;
				} catch (error) {
					console.error("Failed to update appointment status", error);
				}
			}

			console.log(`Starting consultation with room ID: ${roomId}`);
			onStartConsultation(finalAppointment);
			setActiveTab("Appointment");
		}
	};

	const handleSubmit = async (isSendNotification: boolean, acknowledgeUnavailable = false) => {
		// Validation
		if (!updatedAppointment.end_time || !updatedAppointment.start_time) {
			setErrorMessages(["You must fill in start and end times"]);
			return;
		}

		if (selectedPatient.id === "") {
			setErrorMessages(["You must select a patient"]);
			return;
		}

		if (!selectedTeamMember) {
			setErrorMessages(["You must select a practitioner"]);
			return;
		}

		// Appointment type validation
		if (!updatedAppointment.appointment_type_id) {
			setErrorMessages(["You must select an appointment type"]);
			return;
		}

		const appointmentToSubmit: AppointmentSaveRequest = { ...updatedAppointment };

		// Don't override Cliniko consultation links
		if (!appointmentToSubmit.created_in_cliniko) {
			if (appointmentToSubmit.location === "In Person" && !appointmentToSubmit.room_number) {
				appointmentToSubmit.room_number = generateRoomId();
			}

			if (appointmentToSubmit.location === "Online" && !appointmentToSubmit.consultation_link) {
				appointmentToSubmit.consultation_link = generateConsultationLink();
			}
		}

		appointmentToSubmit.patient_id = selectedPatient.id;
		appointmentToSubmit.practitioner_id = selectedTeamMember.id;
		// healthcare owns the send, so the per-appointment-type template applies everywhere
		appointmentToSubmit.send_confirmation = isSendNotification;

		setIsSubmitting(true);
		try {
			let id;
			if (appointmentToSubmit.id === 0) {
				const { data, message: msg } = await setNewAppointment(appointmentToSubmit, acknowledgeUnavailable);
				message.success(`Appointment Created: ${msg}`);
				setSelectedAppointment(data);
				setUpdatedAppointment(data);
				id = data.id;
				// Check guidance status for the new appointment
				checkGuidanceStatus(data.id);
			} else {
				const { data, message: msg } = await updateAppointment(appointmentToSubmit, acknowledgeUnavailable);
				message.success(`Appointment Updated: ${msg}`);
				setUpdatedAppointment(data);
			}

			onClose(id);
		} catch (error: any) {
			const blocks = unavailableBlocksFrom(error);

			if (blocks && !acknowledgeUnavailable) {
				confirmUnavailableOverride(blocks, () => handleSubmit(isSendNotification, true));
				return;
			}

			handleErrors(error);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleAddSecondaryPractitioner = async (practitionerId: UserUuid, timingPosition: 'before' | 'during' | 'after', timingMinutes: number) => {
		if (!updatedAppointment.id) return;
		try {
			const record = await addPractitionerToAppointment(updatedAppointment.id, {
				practitioner_id: practitionerId,
				role: 'secondary',
				timing_position: timingPosition,
				timing_minutes: timingMinutes,
			});
			setSecondaryPractitioners(prev => [...prev.filter(p => p.practitioner_id !== practitionerId), record]);
		} catch (err: any) {
			message.error(err?.response?.data?.message || 'Failed to add secondary practitioner');
		}
	};

	const handleRemoveSecondaryPractitioner = async (practitionerId: UserUuid) => {
		if (!updatedAppointment.id) return;
		try {
			await removePractitionerFromAppointment(updatedAppointment.id, practitionerId);
			setSecondaryPractitioners(prev => prev.filter(p => p.practitioner_id !== practitionerId));
		} catch (err: any) {
			message.error(err?.response?.data?.message || 'Failed to remove secondary practitioner');
		}
	};

	const handleErrors = (error: unknown) => {
		if (axios.isAxiosError(error) && error.response) {
			if (error.response.data?.errors) {
				const messages = Object.entries(error.response.data.errors).flatMap(([_, errs]) => errs as string);
				setErrorMessages(messages);
			} else if (error.response.data?.message) {
				setErrorMessages([error.response.data.message]);
			} else {
				setErrorMessages(["An unknown error occurred."]);
			}
		} else if (error instanceof Error) {
			setErrorMessages([error.message || "An unknown error occurred."]);
		} else {
			setErrorMessages(["An unknown error occurred."]);
		}
	};

	const openPatientModal = () => {
		setPatientModalOpen(true);
	};

	const closePatientModal= async () => {
		setActiveTab("Appointment");
		setPatientModalOpen(false);
	};

	const openPractitionerModal = () => {
		setPractitionerModalOpen(true);
	};

	const closePractitionerModal = async () => {
		setPractitionerModalOpen(false);
	};

	const handleGenerateConsultation = async (workflowTemplateId?: number) => {
		if (customerAttendee) return;

		if (!updatedAppointment.id || updatedAppointment.id === 0) {
			message.warning("Please save the appointment first");
			return;
		}

		// Check if already generating
		if (guidanceStatus.status === "pending" || guidanceStatus.status === "processing") {
			message.warning("Consultation guidance is already being generated");
			setConsultJobUuid(guidanceStatus.jobUuid);
			setConsultProgressModalVisible(true);
			return;
		}

		// Check if already exists
		if (guidanceStatus.exists && guidanceStatus.status === "completed") {
			Modal.confirm({
				title: "Guidance Already Exists",
				content: "Consultation guidance has already been generated for this appointment. Do you want to generate it again?",
				okText: "Generate Again",
				cancelText: "Cancel",
				onOk: async () => {
					await generateNewGuidance(workflowTemplateId);
				},
			});
			return;
		}

		await generateNewGuidance(workflowTemplateId);
	};

	const generateNewGuidance = async (workflowTemplateId?: number) => {
		setIsGeneratingConsult(true);
		try {
			const response = await generateConsultation(updatedAppointment.id, {
				workflowTemplateId,
				practitionerOverrideId: updatedAppointment.practitioner_id || undefined,
			});
			setConsultJobUuid(response.job_uuid);
			setGuidanceStatus({
				exists: false,
				status: "pending",
				sessionId: null,
				jobUuid: response.job_uuid,
			});
			setConsultProgressModalVisible(true);
			message.success("Consultation generation started");
		} catch (error) {
			console.error("Failed to generate consultation:", error);
			message.error("Failed to start consultation generation");
		} finally {
			setIsGeneratingConsult(false);
		}
	};

	const handleConsultComplete = (sessionId: number) => {
		setConsultProgressModalVisible(false);
		setGuidanceStatus({
			exists: true,
			status: "completed",
			sessionId: sessionId,
			jobUuid: null,
		});
		message.success("Consultation generated successfully");
		// Switch to guidance tab
		setActiveTab("Guidance");
	};

	const handleConsultCancel = () => {
		setConsultProgressModalVisible(false);
		setConsultJobUuid(null);
	};

	const unavailableBlocksFrom = (error: unknown): UnavailableBlock[] | null => {
		if (!axios.isAxiosError(error) || error.response?.status !== 409) return null;

		const data = error.response.data;

		return data?.code === "unavailable_block" ? (data.blocks ?? []) : null;
	};

	// Warn-and-allow: healthcare refuses once with the blocks so the practitioner sees what they
	// are overriding, then accepts the identical save with acknowledge_unavailable.
	const confirmUnavailableOverride = (blocks: UnavailableBlock[], onConfirm: () => void) => {
		Modal.confirm({
			title: "Practitioner marked unavailable",
			content: (
				<div data-testid="appointments-unavailable-blocks">
					<Text>This appointment overlaps time the practitioner is marked unavailable:</Text>
					<ul>
						{blocks.map((block, index) => (
							<li key={index}>
								{dayjs(block.starts_at).format("D MMM YYYY, h:mm A")} – {dayjs(block.ends_at).format("h:mm A")}
								{block.reason ? ` — ${block.reason}` : ""}
							</li>
						))}
					</ul>
					<Text>Book over it anyway?</Text>
				</div>
			),
			okText: "Book anyway",
			okButtonProps: { danger: true },
			cancelText: "Cancel",
			onOk: onConfirm,
		});
	};

	const sendNotificationCheck = () => {
		Modal.confirm({
			title: "Send Notification?",
			content: "Would you like to send a notification to the patient?",
			okText: "Yes",
			cancelText: "No",
			onOk: () => handleSubmit(true),
			onCancel: () => handleSubmit(false),
		});
	};

	const handleCancelAppointment = () => {
		Modal.confirm({
			title: "Cancel Appointment?",
			content: "This will mark the appointment as Cancelled and cancel the patient's scheduled reminders and follow-ups.",
			okText: "Yes, cancel it",
			okButtonProps: { danger: true },
			cancelText: "No",
			onOk: async () => {
				const app = { ...updatedAppointment, status: "Cancelled" };
				setIsSubmitting(true);
				try {
					const { data, message: msg } = await updateAppointment(app);
					setUpdatedAppointment(data);
					setSelectedAppointment(data);
					message.success(`Appointment Cancelled: ${msg}`);
				} catch (error) {
					handleErrors(error);
				} finally {
					setIsSubmitting(false);
				}
			},
		});
	};

	const renderClinikoInfo = () => {
		if (!updatedAppointment.cliniko_id) return null;

		return (
			<Alert
				message={
					<Space>
						<SyncOutlined />
						<Text strong>Cliniko Appointment</Text>
					</Space>
				}
				description={
					<Space direction="vertical" size="small" style={{ width: "100%" }}>
						<div>
							<Text type="secondary" style={{ fontSize: 11 }}>
								Cliniko ID:
							</Text>{" "}
							<Text style={{ fontSize: 11 }}>{updatedAppointment.cliniko_id}</Text>
						</div>
						{updatedAppointment.cliniko_synced_at && (
							<div>
								<Text type="secondary" style={{ fontSize: 11 }}>
									Last Synced:
								</Text>{" "}
								<Text style={{ fontSize: 11 }}>{dayjs(updatedAppointment.cliniko_synced_at).format("DD/MM/YYYY HH:mm")}</Text>
							</div>
						)}
						<div>
							<Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 4 }}>
								Note: Cliniko appointments have limited editing capabilities.
							</Text>
						</div>
					</Space>
				}
				type="info"
				showIcon
				style={{ marginBottom: 12 }}
			/>
		);
	};


	const renderGuidanceStatus = () => {
		if (!updatedAppointment.id || updatedAppointment.id === 0) return null;

		if (checkingGuidance) {
			return (
				<Alert
					message={
						<Space>
							<LoadingOutlined />
							<Text strong>Checking Guidance Status</Text>
						</Space>
					}
					description={<Text style={{ fontSize: 12 }}>Loading consultation guidance information...</Text>}
					type="info"
					showIcon={false}
					style={{ marginBottom: 8 }}
				/>
			);
		}

		// Guidance exists and is completed
		if (guidanceStatus.exists && guidanceStatus.status === "completed") {
			return (
				<Alert
					message={
						<Space>
							<CheckCircleOutlined style={{ color: "#52c41a" }} />
							<Text strong>Consultation Guidance Available</Text>
						</Space>
					}
					description={
						<Space direction="vertical" size="small" style={{ width: "100%" }}>
							<Text style={{ fontSize: 12 }}>AI-powered consultation guidance has been generated for this appointment.</Text>
							<Space size="small">
								<Button type="primary" size="small" icon={<FileTextOutlined />} onClick={() => setActiveTab("Guidance")}>
									View Guidance
								</Button>
							<Button size="small" icon={<FileTextOutlined />} onClick={() => handleGenerateConsultation()} loading={isGeneratingConsult}>
									Regenerate
								</Button>
							</Space>
						</Space>
					}
					type="success"
					showIcon={false}
					style={{ marginBottom: 8 }}
				/>
			);
		}

		// Currently generating
		if (guidanceStatus.status === "pending" || guidanceStatus.status === "processing") {
			return (
				<Alert
					message={
						<Space>
							<LoadingOutlined />
							<Text strong>Generating Consultation Guidance</Text>
						</Space>
					}
					description={
						<Space direction="vertical" size="small" style={{ width: "100%" }}>
							<Text style={{ fontSize: 12 }}>AI is currently analyzing patient data and generating consultation guidance. This may take a few minutes.</Text>
							{guidanceStatus.progress !== undefined && (
								<div>
									<Text type="secondary" style={{ fontSize: 11 }}>
										Progress: {guidanceStatus.progress}%
									</Text>
								</div>
							)}
							<Button
								type="primary"
								size="small"
								icon={<FileTextOutlined />}
								onClick={() => {
									setConsultJobUuid(guidanceStatus.jobUuid);
									setConsultProgressModalVisible(true);
								}}
							>
								View Progress
							</Button>
						</Space>
					}
					type="info"
					showIcon={false}
					style={{ marginBottom: 8 }}
				/>
			);
		}

		// Failed
		if (guidanceStatus.status === "failed") {
			return (
				<Alert
					message={
						<Space>
							<ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />
							<Text strong>Guidance Generation Failed</Text>
						</Space>
					}
					description={
						<Space direction="vertical" size="small" style={{ width: "100%" }}>
							<Text style={{ fontSize: 12 }}>{guidanceStatus.errorMessage || "An error occurred while generating consultation guidance."}</Text>
						<Button type="primary" size="small" icon={<FileTextOutlined />} onClick={() => handleGenerateConsultation()} loading={isGeneratingConsult}>
								Try Again
							</Button>
						</Space>
					}
					type="error"
					showIcon={false}
					style={{ marginBottom: 8 }}
				/>
			);
		}

		// No guidance yet — build list of available workflow options
		const appointmentType = updatedAppointment.appointment_type;
		const practitionerId = updatedAppointment.practitioner_id;

		// Practitioner-specific workflows for the assigned practitioner
		const practitionerWorkflows = (appointmentType?.practitioner_workflows || []).filter(
			(pw) => pw.practitioner_id === practitionerId && pw.is_active
		);

		// General workflow assignments (exclude ones already covered by practitioner workflows)
		const practitionerTemplateIds = new Set(practitionerWorkflows.map((pw) => pw.workflow_template_id));
		const generalWorkflows = (appointmentType?.workflow_assignments || []).filter(
			(wa) => wa.is_active && !practitionerTemplateIds.has(wa.workflow_template_id)
		);

		const hasWorkflowOptions = practitionerWorkflows.length > 0 || generalWorkflows.length > 0;

		return (
			<Alert
				message={
					<Space>
						<FileTextOutlined />
						<Text strong>AI Consultation Guidance</Text>
					</Space>
				}
				description={
					<Space direction="vertical" size="small" style={{ width: "100%" }}>
						<Text style={{ fontSize: 12 }}>Generate comprehensive AI-powered consultation guidance based on patient data, questionnaires, and genetic results.</Text>
						{hasWorkflowOptions ? (
							<>
								{practitionerWorkflows.length > 0 && (
									<Space direction="vertical" size={4} style={{ width: "100%" }}>
										<Text type="secondary" style={{ fontSize: 11 }}>Practitioner Templates</Text>
										{practitionerWorkflows.map((pw) => (
											<Button
												key={pw.id}
												type="primary"
												size="small"
												icon={<FileTextOutlined />}
												loading={isGeneratingConsult}
												onClick={() => handleGenerateConsultation(pw.workflow_template_id)}
												style={{ display: "block" }}
											>
												{pw.workflow_template?.name ?? `Template #${pw.workflow_template_id}`}
											</Button>
										))}
									</Space>
								)}
								{generalWorkflows.length > 0 && (
									<Space direction="vertical" size={4} style={{ width: "100%" }}>
										<Text type="secondary" style={{ fontSize: 11 }}>General Templates</Text>
										{generalWorkflows.map((wa) => (
											<Button
												key={wa.id}
												size="small"
												icon={<FileTextOutlined />}
												loading={isGeneratingConsult}
												onClick={() => handleGenerateConsultation(wa.workflow_template_id)}
												style={{ display: "block" }}
											>
												{wa.workflow_template?.name ?? `Template #${wa.workflow_template_id}`}
											</Button>
										))}
									</Space>
								)}
							</>
						) : (
							<Button type="primary" size="small" icon={<FileTextOutlined />} onClick={() => handleGenerateConsultation()} loading={isGeneratingConsult}>
								Generate Consultation Guidance
							</Button>
						)}
					</Space>
				}
				type="info"
				showIcon={false}
				style={{ marginBottom: 16 }}
			/>
		);
	};

	return (
		<>
			<DetailDrawer
				open={isOpen}
				onClose={() => onClose()}
				width="85vw"
				title={
					<Space size={8} data-testid="appointments-drawer">
						{updatedAppointment.id === 0 ? "New Appointment" : updatedAppointment.title || "Edit Appointment"}
						{updatedAppointment.cliniko_id && (
							<Tag color="blue" icon={<SyncOutlined />} style={{ fontSize: 11 }}>
								Cliniko
							</Tag>
						)}
					</Space>
				}
				headerActions={
					<Space size={8}>
						{updatedAppointment.id !== 0 && (() => {
							return (
								<>
									<Button
										type="default"
										icon={<UserOutlined />}
										size="small"
										onClick={openPatientModal}
										data-testid="appointments-patient-btn"
									>
										Patient
									</Button>
									<Button
										type="default"
										icon={<UserOutlined />}
										size="small"
										onClick={openPractitionerModal}
										disabled={!selectedTeamMember}
										data-testid="appointments-practitioner-btn"
									>
										Practitioner
									</Button>
								</>
							);
						})()}
						{updatedAppointment.id !== 0 && (() => {
							const isClinikoOnline = !!updatedAppointment.cliniko_id && updatedAppointment.cliniko_booking_link && (updatedAppointment.location === "Online" || updatedAppointment.location === "online");
							const status = updatedAppointment.status?.toLowerCase();
							let label = isClinikoOnline ? "Start in Cliniko" : "Start Now";
							let icon: React.ReactNode = isClinikoOnline ? <LinkOutlined /> : <PlayCircleOutlined />;
							if (status === "completed" || status === "post consult completed") { label = "View Report"; icon = <FileTextOutlined />; }
							else if (status === "in consultation") { label = isClinikoOnline ? "Open Cliniko" : "Resume"; }
							else if (status === "cancelled" || status === "no show") { label = "Reschedule"; icon = <CalendarOutlined />; }
							const handleStart = () => {
								if (status === "completed" || status === "post consult completed") {
									setActiveTab("Report");
								} else {
									handleStartConsultation();
								}
							};
							const viewingReport = status === "completed" || status === "post consult completed";
							return (
								<Button
									type={viewingReport ? "default" : "primary"}
									icon={icon}
									size="small"
									onClick={handleStart}
									disabled={customerAttendee && !viewingReport}
									title={customerAttendee && !viewingReport ? "Consultations are available only for patients" : undefined}
									data-testid={status === "cancelled" || status === "no show" ? "appointments-reschedule-btn" : "appointments-start-btn"}
								>
									{label}
								</Button>
							);
						})()}
						{updatedAppointment.id !== 0 && !updatedAppointment.created_in_cliniko &&
							!["cancelled", "cancelled within 24h", "no show"].includes(updatedAppointment.status?.toLowerCase() ?? "") && (
							<Button
								danger
								icon={<CloseCircleOutlined />}
								size="small"
								loading={isSubmitting}
								onClick={handleCancelAppointment}
								data-testid="appointments-cancel-btn"
							>
								Cancel
							</Button>
						)}
						<Button
							type="primary"
							icon={<SaveOutlined />}
							loading={isSubmitting}
							onClick={sendNotificationCheck}
							size="small"
							disabled={updatedAppointment.created_in_cliniko}
							data-testid="appointments-save-btn"
						>
							Save
						</Button>
					</Space>
				}
				errorMessages={errorMessages}
				onClearErrors={() => setErrorMessages([])}
				activeTab={activeTab}
				onTabChange={setActiveTab}
				tabs={(() => {
					const apptType = updatedAppointment.appointment_type;
					const practId = updatedAppointment.practitioner_id;
					const hasPractitionerWorkflows = (apptType?.practitioner_workflows || []).some(
						(pw) => pw.practitioner_id === practId && pw.is_active
					);
					const hasGeneralWorkflows = (apptType?.workflow_assignments || []).some((wa) => wa.is_active);
					const appointmentHasWorkflows = hasPractitionerWorkflows || hasGeneralWorkflows;

					const tabList: Array<{ key: string; label: string; children: React.ReactNode }> = [
						{
							key: "Appointment",
							label: "Appointment",
							children: (
								<>
									{renderClinikoInfo()}
									{customerAttendee && (
										<Alert
											type="info"
											showIcon
											style={{ marginBottom: 8 }}
											data-testid="appointments-customer-consult-restriction"
											message="This appointment is booked for a customer account"
											description="You can still manage the booking. Consultations, including clinical notes, patient details, transcription and AI suggestions, are available only for patients: convert the customer to a patient from Inventory → Customers first."
										/>
									)}
									{atCapUsage && (
										<Alert
											type="warning"
											showIcon
											style={{ marginBottom: 8 }}
											data-testid="appointments-at-cap-warning"
											message={`This patient has used all ${atCapUsage.max_bookings_per_patient} of their ${atCapUsage.name} bookings. You can still book on their behalf.`}
										/>
									)}
									<ScreeningPanel appointmentId={updatedAppointment.id} />
									{appointmentHasWorkflows && !customerAttendee && renderGuidanceStatus()}
									<GeneralTab
										isLock={isLock || !!updatedAppointment.created_in_cliniko}
										appointment={updatedAppointment}
										setAppointment={setUpdatedAppointment}
										selectedPatient={selectedPatient}
										selectedTeamMember={selectedTeamMember}
										setSelectedPatient={setSelectedPatient}
										setSelectedTeamMember={setSelectedTeamMember}
										teamMembers={teamMembers}
										secondaryPractitioners={secondaryPractitioners}
										onAddSecondaryPractitioner={handleAddSecondaryPractitioner}
										onRemoveSecondaryPractitioner={handleRemoveSecondaryPractitioner}
										appointmentTypes={appointmentTypes}
										setAppointmentTypes={setAppointmentTypes}
										selectedAppointmentType={selectedAppointmentType}
										setSelectedAppointmentType={setSelectedAppointmentType}
									/>
								</>
							),
						},
					];

					if (updatedAppointment.id !== 0) {
						if (appointmentHasWorkflows || (guidanceStatus.exists && guidanceStatus.status === "completed")) {
							tabList.push({
								key: "Guidance",
								label: "Guidance",
								children: (
									<div
										style={{
											height: "calc(100vh - 220px)",
											border: "1px solid var(--mg-border)",
											borderRadius: 8,
											overflow: "hidden",
										}}
									>
										<ConsultGuidancePanel sessionId={guidanceStatus.sessionId} appointmentId={updatedAppointment.id} isDisabled={customerAttendee} />
									</div>
								),
							});
						}

						if (completedConsult) {
							tabList.push({
								key: "Report",
								label: "Consult Report",
								children: (
									<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
										<div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
											{completedConsult.diagnosis && (
												<div style={{ flex: 1, minWidth: 200, padding: 12, border: "1px solid var(--mg-border)", borderRadius: 8 }}>
													<div style={{ fontSize: 11, color: "var(--mg-text-secondary)", marginBottom: 4 }}>Diagnosis</div>
													<div style={{ fontSize: 13, fontWeight: 500 }}>{completedConsult.diagnosis}</div>
												</div>
											)}
											<div style={{ flex: 1, minWidth: 200, padding: 12, border: "1px solid var(--mg-border)", borderRadius: 8 }} data-testid="appointments-vitals-panel">
												<div style={{ fontSize: 11, color: "var(--mg-text-secondary)", marginBottom: 4 }}>Vitals</div>
												{consultVitalsReadings.length > 0 ? (
													<div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
														{consultVitalsReadings.map((r) => (
															<span
																key={r.key}
																style={{
																	fontSize: 11,
																	padding: "1px 6px",
																	borderRadius: 3,
																	border: r.outOfRange ? "1px solid var(--mg-danger)" : "1px solid var(--mg-border)",
																	background: r.outOfRange ? "var(--mg-danger-bg)" : "transparent",
																	color: r.outOfRange ? "var(--mg-danger)" : "var(--mg-text)",
																}}
															>
																<span style={{ color: "var(--mg-text-secondary)" }}>{r.label} </span>
																{r.display}
																{r.unit ? ` ${r.unit}` : ""}
															</span>
														))}
													</div>
												) : (
													<div style={{ fontSize: 13, color: "var(--mg-text-disabled)" }}>No vitals recorded</div>
												)}
											</div>
											{completedConsult.notes && (
												<div style={{ flex: 1, minWidth: 200, padding: 12, border: "1px solid var(--mg-border)", borderRadius: 8 }}>
													<div style={{ fontSize: 11, color: "var(--mg-text-secondary)", marginBottom: 4 }}>Notes</div>
													<div style={{ fontSize: 13 }} dangerouslySetInnerHTML={{ __html: completedConsult.notes }} />
												</div>
											)}
										</div>
										{completedConsult.report ? (
											<div
												style={{
													border: "1px solid var(--mg-border)",
													borderRadius: 8,
													padding: 24,
													background: "var(--mg-bg-card)",
													lineHeight: 1.6,
													fontSize: 13,
												}}
												dangerouslySetInnerHTML={{ __html: completedConsult.report }}
											/>
										) : (
											<div
												style={{
													padding: 24,
													textAlign: "center",
													color: "var(--mg-text-disabled)",
													border: "1px dashed var(--mg-border)",
													borderRadius: 8,
												}}
											>
												No report was generated for this consultation.
											</div>
										)}
										{archivedChat.length > 0 && (
											<div data-testid="appointments-consult-chat">
												<Collapse
													defaultActiveKey={["chat"]}
													items={[
														{
															key: "chat",
															label: `Consultation chat (${archivedChat.length})`,
															children: <ArchivedChatView messages={archivedChat} />,
														},
													]}
												/>
											</div>
										)}
									</div>
								),
							});

							tabList.push({
								key: "Transcript",
								label: `Transcript (${archivedTranscript.length})`,
								children: (
									<div data-testid="appointments-consult-transcript">
										{recordingFileId ? (
											<ConsultRecordingPlayer key={recordingFileId} patientId={selectedAppointment.patient_id} fileId={recordingFileId} appId={updatedAppointment.id} seekTo={seekTarget} onTimeUpdate={setPlayhead} onReady={markRecordingReady} />
										) : recordingPending ? (
											<div data-testid="consult-recording-pending" style={{ fontSize: 13, color: "var(--mg-text-secondary)", marginBottom: 12 }}>
												Recording will be available once the final transcript is ready.
											</div>
										) : (
											<div data-testid="consult-recording-none" style={{ fontSize: 13, color: "var(--mg-text-secondary)", marginBottom: 12 }}>
												No recording on file for this consultation.
											</div>
										)}
										<ArchivedTranscriptView
											transcripts={archivedTranscript}
											speakerMap={archivedSpeakerMap}
											displayNames={transcriptRoster}
											status={completedConsult.transcript_status}
											onSegmentSelect={canSeek ? handleSegmentSelect : undefined}
											activeSegmentId={activeSegmentId}
										/>
									</div>
								),
							});
						}
					}

					return tabList;
				})()}
			/>
			{/* Mounted on demand: both hosts open this drawer with selectedTeamMember still null, and EditUserModal reads its user at mount. */}
			{patientModalOpen && <EditPatientModal isOpen onClose={closePatientModal} patient={selectedPatient} />}
			{practitionerModalOpen && selectedTeamMember && (
				<EditUserModal isOpen onClose={closePractitionerModal} user={selectedTeamMember} mode="team" onSaved={closePractitionerModal} />
			)}
			<ConsultProgressModal visible={consultProgressModalVisible} jobUuid={consultJobUuid} onComplete={handleConsultComplete} onCancel={handleConsultCancel} />
		</>
	);
};

export default AppointmentDrawer;
